import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import { C, fmtINR } from "./ipd-billing/constants";
import SvgIcon from "./ipd-billing/SvgIcon";
import IPDBillingLayout from "./ipd-billing/components/IPDBillingLayout";
import { resolveAdmissionId, toBillingError } from "./ipd-billing/billing-api";
import {
  ensureBillingSetup,
  getPackageQuickHeader,
  getPackageAssignment,
  getEntitlements,
  getUtilisation,
  getChargeSheet,
  cancelPackageAssignment,
  completePackageAssignment,
} from "./ipd-billing/package-billing-api";

// ─────────────────────────────────────────────────────────────────────────────
// Package Dashboard (Module 5) — patient-scoped read view of an active package
// assignment: header card, entitlements/utilisation table, charge-sheet grid
// with IN_PACKAGE/OUT_OF_PACKAGE badges, totals, and Cancel/Complete actions.
// Reached from the "Package: CODE" badge on Charge Capture, which passes
// {admissionId, assignmentId, billingAccountId, patientName} via route state.
// Falls back to re-deriving ids from admissionId (or sessionStorage) so a
// direct link / refresh still works without that state.
// ─────────────────────────────────────────────────────────────────────────────

const STATUS_TONE = {
  ACTIVE: { bg: "#d1f2db", color: "#145a32" },
  COMPLETED: { bg: "#e0e7ff", color: "#4338ca" },
  CANCELLED: { bg: "#fdecea", color: "#842029" },
};

function StatusBadge({ status }) {
  const tone = STATUS_TONE[status] || { bg: "#f1f5f9", color: "#475569" };
  return (
    <span style={{ display: "inline-block", padding: "3px 10px", borderRadius: "999px", fontSize: "11px", fontWeight: 700, background: tone.bg, color: tone.color }}>
      {status || "—"}
    </span>
  );
}

function AllocationBadge({ inPackage }) {
  return inPackage ? (
    <span style={{ padding: "2px 9px", borderRadius: "999px", fontSize: "11px", fontWeight: 700, background: "#d1f2db", color: "#145a32", whiteSpace: "nowrap" }}>
      Included
    </span>
  ) : (
    <span style={{ padding: "2px 9px", borderRadius: "999px", fontSize: "11px", fontWeight: 700, background: "#fff3cd", color: "#856404", whiteSpace: "nowrap" }}>
      Extra
    </span>
  );
}

function fmtDate(d) {
  if (!d) return "—";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return String(d);
  return dt.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

const labelStyle = { fontSize: "11px", fontWeight: "700", color: C.textSub, textTransform: "uppercase", letterSpacing: "0.06em", display: "block", marginBottom: "5px" };
const inputStyle = { width: "100%", padding: "9px 12px", border: `1px solid ${C.border}`, borderRadius: "7px", fontSize: "13px", color: C.text, outline: "none", boxSizing: "border-box", background: "#fff" };
const btnGhost = { flex: 1, padding: "11px", background: "#fff", color: C.textSub, border: `1px solid ${C.border}`, borderRadius: "8px", fontSize: "14px", fontWeight: "600", cursor: "pointer" };
const btnDanger = (busy) => ({ flex: 2, padding: "11px", background: C.danger, color: "#fff", border: "none", borderRadius: "8px", fontSize: "14px", fontWeight: "700", cursor: busy ? "wait" : "pointer" });
const btnPrimary = (busy) => ({ flex: 2, padding: "11px", background: C.primary, color: "#fff", border: "none", borderRadius: "8px", fontSize: "14px", fontWeight: "700", cursor: busy ? "wait" : "pointer" });

// Hand-rolled modal shell, matching the shape used in PackageAssignModal.js /
// PaymentsReceiptsPanel.js — not a shared import in this codebase's convention.
function ModalShell({ title, subtitle, onClose, children, footer, width = 440 }) {
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(13,35,63,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: "20px" }} onClick={onClose}>
      <div style={{ background: "#fff", borderRadius: "16px", padding: "28px", width: `${width}px`, maxWidth: "92vw", boxShadow: "0 24px 64px rgba(13,35,63,0.28)" }} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ margin: "0 0 6px", fontSize: "17px", fontWeight: "800", color: C.text }}>{title}</h3>
        {subtitle && <p style={{ margin: "0 0 18px", fontSize: "12px", color: C.textSub }}>{subtitle}</p>}
        <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>{children}</div>
        <div style={{ display: "flex", gap: "10px", marginTop: "22px" }}>{footer}</div>
      </div>
    </div>
  );
}

function CancelCompleteModal({ mode, onClose, onSubmit, busy }) {
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const isCancel = mode === "cancel";

  const submit = () => {
    if (isCancel && !reason.trim()) { setError("Cancellation reason is required."); return; }
    onSubmit(reason.trim());
  };

  return (
    <ModalShell
      title={isCancel ? "Cancel Package Assignment" : "Complete Package Assignment"}
      subtitle={isCancel ? "This stops further charges from being evaluated against the package." : "Marks the package assignment as fulfilled."}
      onClose={onClose}
      footer={<>
        <button onClick={onClose} disabled={busy} style={btnGhost}>Back</button>
        <button onClick={submit} disabled={busy} style={isCancel ? btnDanger(busy) : btnPrimary(busy)}>
          {busy ? "Submitting…" : isCancel ? "Cancel Assignment" : "Complete Assignment"}
        </button>
      </>}
    >
      <div>
        <label style={labelStyle}>{isCancel ? "Cancellation reason" : "Remarks (optional)"}{isCancel && <span style={{ color: C.danger }}> *</span>}</label>
        <textarea value={reason} onChange={(e) => { setReason(e.target.value); setError(""); }} rows={3}
          placeholder={isCancel ? "Why is this assignment being cancelled?" : "Optional note"} style={{ ...inputStyle, resize: "vertical" }} />
      </div>
      {error && <div style={{ fontSize: "12px", color: C.danger, fontWeight: "600" }}>{error}</div>}
    </ModalShell>
  );
}

export default function PackageDashboard() {
  const location = useLocation();
  const history = useHistory();
  const state = location.state || {};

  const admissionId = state.admissionId || resolveAdmissionId(null);
  const [billingAccountId, setBillingAccountId] = useState(state.billingAccountId || null);
  const [assignmentId, setAssignmentId] = useState(state.assignmentId || null);

  const [assignment, setAssignment] = useState(null);
  const [entitlements, setEntitlements] = useState([]);
  const [utilisation, setUtilisation] = useState([]);
  const [chargeSheet, setChargeSheet] = useState(null);
  const [chargeSheetError, setChargeSheetError] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionMode, setActionMode] = useState(null); // "cancel" | "complete" | null
  const [actionBusy, setActionBusy] = useState(false);

  const resolveIds = useCallback(async () => {
    if (billingAccountId && assignmentId) return { billingAccountId, assignmentId };
    if (!admissionId) throw new Error("No admission selected — open this page from a patient's Charge Capture screen.");
    const setup = await ensureBillingSetup(admissionId);
    const quick = setup.billingAccountId ? await getPackageQuickHeader(setup.billingAccountId) : null;
    if (!quick) throw new Error("No package is assigned to this admission.");
    setBillingAccountId(setup.billingAccountId);
    setAssignmentId(quick.assignmentId);
    return { billingAccountId: setup.billingAccountId, assignmentId: quick.assignmentId };
  }, [admissionId, billingAccountId, assignmentId]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const ids = await resolveIds();
      // The charge sheet is billing-account-scoped, not assignment-scoped, and the
      // backend still resolves a single ACTIVE assignment per account — so it 500s
      // once a second package is assigned. Kick it off in parallel but never let it
      // fail the whole page; the rest of the dashboard is assignment-scoped and fine.
      const sheetPromise = getChargeSheet(ids.billingAccountId).catch((e) => e);
      const [a, ent, util] = await Promise.all([
        getPackageAssignment(ids.assignmentId),
        getEntitlements(ids.assignmentId),
        getUtilisation(ids.assignmentId),
      ]);
      setAssignment(a);
      setEntitlements(ent);
      setUtilisation(util);
      const sheet = await sheetPromise;
      setChargeSheet(sheet instanceof Error ? null : sheet);
      setChargeSheetError(sheet instanceof Error ? toBillingError(sheet, "Could not load the charge sheet") : "");
    } catch (err) {
      setError(toBillingError(err, "Could not load package"));
    } finally {
      setLoading(false);
    }
  }, [resolveIds]);

  useEffect(() => { load(); }, [load]);

  // Merge frozen entitlement rules with live consumption by service code.
  const utilByCode = new Map(utilisation.map((u) => [u.serviceCode, u]));
  const entitlementRows = entitlements.map((e) => {
    const u = utilByCode.get(e.serviceCode) || {};
    return {
      ...e,
      consumedQuantity: u.consumedQuantity ?? e.consumedQuantity,
      remainingQuantity: u.remainingQuantity ?? e.remainingQuantity,
      consumedAmount: u.consumedAmount ?? e.consumedAmount,
      remainingAmount: u.remainingAmount ?? e.remainingAmount,
    };
  });

  const limitLabel = (e) => {
    if (e.limitType === "ITEM_LIMIT") return `${e.consumedQuantity ?? 0} / ${e.originalItemLimit ?? "—"} used`;
    if (e.limitType === "AMOUNT_LIMIT") return `${fmtINR(e.consumedAmount ?? 0)} / ${fmtINR(e.amountLimit ?? 0)}`;
    return "No limit";
  };

  const runAction = async (reasonOrRemarks) => {
    setActionBusy(true);
    try {
      if (actionMode === "cancel") {
        await cancelPackageAssignment(assignmentId, reasonOrRemarks);
        toast.success("Package assignment cancelled");
      } else {
        await completePackageAssignment(assignmentId, reasonOrRemarks);
        toast.success("Package assignment completed");
      }
      setActionMode(null);
      await load();
    } catch (err) {
      toast.error(toBillingError(err, "Could not update assignment"));
    } finally {
      setActionBusy(false);
    }
  };

  const isActive = assignment?.status === "ACTIVE";

  return (
    <IPDBillingLayout activeNav="charge">
      <div style={{ padding: "28px", display: "flex", flexDirection: "column", gap: "20px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <button
            onClick={() => history.goBack()}
            style={{ display: "flex", alignItems: "center", gap: "6px", padding: "7px 12px", background: "#fff", border: `1px solid ${C.border}`, borderRadius: "8px", color: C.text, fontSize: "13px", fontWeight: 600, cursor: "pointer" }}
          >
            <SvgIcon name="back" size={16} color={C.text} /> Back
          </button>
          <div>
            <h2 style={{ margin: "0 0 2px", fontSize: "20px", fontWeight: "800", color: C.text }}>Package Dashboard</h2>
            {state.patientName && <p style={{ margin: 0, fontSize: "13px", color: C.textSub }}>{state.patientName} · Encounter {admissionId}</p>}
          </div>
        </div>

        {loading ? (
          <div style={{ background: "#fff", borderRadius: "12px", border: `1px solid ${C.border}`, padding: "40px", textAlign: "center", color: C.textSub, fontSize: "13px" }}>
            Loading package…
          </div>
        ) : error ? (
          <div style={{ background: "#fdecea", border: "1px solid #f5c6cb", borderRadius: "10px", padding: "16px 20px", color: "#842029", fontSize: "13px", fontWeight: 600 }}>
            {error}
          </div>
        ) : (
          <>
            {/* Header card */}
            <div style={{ background: "#fff", borderRadius: "12px", border: `1px solid ${C.border}`, padding: "22px 24px", display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "16px" }}>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "6px" }}>
                  <span style={{ fontSize: "18px", fontWeight: "800", color: C.text }}>{assignment?.packageName || assignment?.packageCode}</span>
                  <StatusBadge status={assignment?.status} />
                </div>
                <div style={{ fontSize: "12px", color: C.textSub }}>
                  {assignment?.packageCode} · {assignment?.assignmentNumber} · Assigned {fmtDate(assignment?.assignedAt)}
                </div>
                {assignment?.cancelledAt && (
                  <div style={{ fontSize: "12px", color: C.danger, marginTop: "4px" }}>
                    Cancelled {fmtDate(assignment.cancelledAt)}{assignment.cancellationReason ? ` — ${assignment.cancellationReason}` : ""}
                  </div>
                )}
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: "11px", fontWeight: 700, color: C.textSub, textTransform: "uppercase" }}>Applied price</div>
                <div style={{ fontSize: "24px", fontWeight: "800", color: C.text }}>{fmtINR(assignment?.appliedPackagePrice ?? 0)}</div>
                {assignment?.standardPackagePrice != null && assignment.standardPackagePrice !== assignment.appliedPackagePrice && (
                  <div style={{ fontSize: "11px", color: C.textSub, textDecoration: "line-through" }}>{fmtINR(assignment.standardPackagePrice)}</div>
                )}
              </div>
              {isActive && (
                <div style={{ display: "flex", gap: "8px", width: "100%" }}>
                  <button onClick={() => setActionMode("complete")} style={{ ...btnGhost, flex: "none", padding: "8px 14px" }}>Complete assignment</button>
                  <button onClick={() => setActionMode("cancel")} style={{ ...btnGhost, flex: "none", padding: "8px 14px", color: C.danger, borderColor: "#f5c6cb" }}>Cancel assignment</button>
                </div>
              )}
            </div>

            {/* Entitlements table */}
            <div style={{ background: "#fff", borderRadius: "12px", border: `1px solid ${C.border}`, overflow: "hidden" }}>
              <div style={{ padding: "14px 20px", borderBottom: `1px solid ${C.border}` }}>
                <h3 style={{ margin: 0, fontSize: "14px", fontWeight: "700", color: C.text }}>Entitlements</h3>
                <p style={{ margin: "2px 0 0", fontSize: "12px", color: C.textSub }}>Frozen at assignment — master edits since then don't apply here.</p>
              </div>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "600px" }}>
                  <thead>
                    <tr style={{ background: "#f8fafc" }}>
                      {["Service", "Inclusion", "Usage", ""].map((h) => (
                        <th key={h} style={{ textAlign: "left", padding: "10px 14px", fontSize: "11px", fontWeight: 700, color: C.textSub, textTransform: "uppercase", borderBottom: `1px solid ${C.border}` }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {entitlementRows.length === 0 ? (
                      <tr><td colSpan={4} style={{ padding: "22px", textAlign: "center", color: C.textSub, fontSize: "13px" }}>No entitlements on this package.</td></tr>
                    ) : (
                      entitlementRows.map((e) => (
                        <tr key={e.id || e.serviceCode} style={{ borderBottom: `1px solid ${C.border}` }}>
                          <td style={{ padding: "9px 14px", fontSize: "13px", color: C.text }}>{e.referenceName}</td>
                          <td style={{ padding: "9px 14px" }}>
                            <span style={{ padding: "2px 8px", borderRadius: "999px", fontSize: "11px", fontWeight: 700, background: e.inclusionType === "INCLUDE" ? "#d1f2db" : "#fdecea", color: e.inclusionType === "INCLUDE" ? "#145a32" : "#842029" }}>
                              {e.inclusionType}
                            </span>
                          </td>
                          <td style={{ padding: "9px 14px", fontSize: "12px", color: C.textSub }}>{limitLabel(e)}</td>
                          <td style={{ padding: "9px 14px", fontSize: "12px", color: C.textSub, textAlign: "right" }}>
                            {e.remainingQuantity != null ? `${e.remainingQuantity} left` : ""}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Charge sheet */}
            <div style={{ background: "#fff", borderRadius: "12px", border: `1px solid ${C.border}`, overflow: "hidden" }}>
              <div style={{ padding: "14px 20px", borderBottom: `1px solid ${C.border}` }}>
                <h3 style={{ margin: 0, fontSize: "14px", fontWeight: "700", color: C.text }}>Charge Sheet</h3>
              </div>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "600px" }}>
                  <thead>
                    <tr style={{ background: "#f8fafc" }}>
                      {["Service", "Qty", "Amount", "Allocation"].map((h) => (
                        <th key={h} style={{ textAlign: h === "Service" ? "left" : "right", padding: "10px 14px", fontSize: "11px", fontWeight: 700, color: C.textSub, textTransform: "uppercase", borderBottom: `1px solid ${C.border}` }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {chargeSheetError ? (
                      <tr><td colSpan={4} style={{ padding: "22px", textAlign: "center", color: C.danger, fontSize: "13px" }}>
                        {chargeSheetError}
                        <div style={{ marginTop: "4px", color: C.textSub, fontSize: "12px", fontWeight: 400 }}>
                          Entitlements and utilisation above are unaffected.
                        </div>
                      </td></tr>
                    ) : !chargeSheet || chargeSheet.items.length === 0 ? (
                      <tr><td colSpan={4} style={{ padding: "22px", textAlign: "center", color: C.textSub, fontSize: "13px" }}>No charges posted yet.</td></tr>
                    ) : (
                      chargeSheet.items.map((it) => (
                        <tr key={it.chargeEventId} style={{ borderBottom: `1px solid ${C.border}` }}>
                          <td style={{ padding: "9px 14px", fontSize: "13px", color: C.text }}>{it.serviceName || it.serviceCode}</td>
                          <td style={{ padding: "9px 14px", fontSize: "13px", color: C.textSub, textAlign: "right" }}>{it.quantity}</td>
                          <td style={{ padding: "9px 14px", fontSize: "13px", fontWeight: 700, color: C.text, textAlign: "right" }}>{fmtINR(it.grossAmount)}</td>
                          <td style={{ padding: "9px 14px", textAlign: "right" }}><AllocationBadge inPackage={it.isPackageCharge} /></td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              {chargeSheet && (
                <div style={{ display: "flex", justifyContent: "flex-end", gap: "24px", padding: "14px 20px", borderTop: `1px solid ${C.border}`, background: "#f8fafc" }}>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: "10px", fontWeight: 700, color: C.textSub, textTransform: "uppercase" }}>In-package</div>
                    <div style={{ fontSize: "15px", fontWeight: "800", color: "#145a32" }}>{fmtINR(chargeSheet.inPackageTotal)}</div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: "10px", fontWeight: 700, color: C.textSub, textTransform: "uppercase" }}>Out-of-package</div>
                    <div style={{ fontSize: "15px", fontWeight: "800", color: "#856404" }}>{fmtINR(chargeSheet.outOfPackageTotal)}</div>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {actionMode && (
        <CancelCompleteModal
          mode={actionMode}
          busy={actionBusy}
          onClose={() => setActionMode(null)}
          onSubmit={runAction}
        />
      )}
    </IPDBillingLayout>
  );
}
